import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  HostListener,
  Inject,
  ViewChild,
  ElementRef,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageRotationService } from './image-rotation.service';
import { ActionButtonComponent, ButtonAction, ButtonType } from '../action-button';
import { MaterialModule } from '../../material.module';
import { IconNames } from '../../common/constants';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-carousel-gallery',
  imports: [MaterialModule, CommonModule, ActionButtonComponent, SvgIconComponent],
  templateUrl: './carousel-gallery.component.html',
  styleUrls: ['./carousel-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselGalleryComponent implements OnInit {
  @ViewChild('image') image!: ElementRef<HTMLImageElement>;
  imageList = new BehaviorSubject<string[]>(null);
  imageSrc: string;
  slideIndex: number = null;
  isRotationAllowed: boolean;
  turnAngle = 0;
  rotatedURL: string;
  buttonType = ButtonType;
  buttonAction = ButtonAction;
  iconNames = IconNames;

  readonly increment = 1;
  readonly decrement = -1;

  public closeButtonClicked: EventEmitter<void> = new EventEmitter<void>();

  @HostListener('document:keydown.arrowleft') prevAttach(): void {
    this.prev(this.decrement);
  }

  @HostListener('document:keydown.arrowright') nextAttach(): void {
    this.next(this.increment);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      imageList: string[];
      imageIndex: number;
      imageSrc: string;
      isRotationAllowed: boolean;
    },
    private dialogRef: MatDialogRef<CarouselGalleryComponent>,
    private imageRotationService: ImageRotationService,
  ) {}

  ngOnInit(): void {
    this.imageSrc = this.data.imageSrc;
    this.imageList.next(this.data.imageList);
    this.slideIndex = this.data.imageIndex;
    this.isRotationAllowed = this.data.isRotationAllowed;
  }

  prev(i: number): void {
    this.slideIndex = this.slideIndex += i;

    if (this.slideIndex < 0) {
      this.slideIndex = this.imageList.getValue()?.length - 1;
    }
    this.updateImageSrc();
  }

  next(i: number): void {
    this.slideIndex += i;

    if (this.slideIndex > this.imageList.getValue()?.length - 1) {
      this.slideIndex = 0;
    }
    this.updateImageSrc();
  }

  rotateImage(degrees: number): void {
    this.turnAngle = (this.turnAngle + degrees) % 360;
    const img = this.image.nativeElement;
    img.setAttribute('style', 'transform: rotate(' + this.turnAngle + 'deg)');

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0px';
    canvas.style.left = '0px';
    const ctx = canvas.getContext('2d');

    const radians = (this.turnAngle * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);

    const canvasWidth = Math.abs(img.width * cos) + Math.abs(img.height * sin);
    const canvasHeight = Math.abs(img.width * sin) + Math.abs(img.height * cos);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.translate(canvasWidth / 2, canvasHeight / 2);
    ctx.rotate(radians);

    ctx.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height);

    this.rotatedURL = canvas.toDataURL('image/jpeg');

    const wrapper = document.querySelector('.image-wrapper__image');
    wrapper.setAttribute('style', 'width:' + canvas.width + 'px');
    wrapper.setAttribute('style', 'height:' + canvas.height + 'px');
  }

  saveImageRotation(): void {
    this.imageRotationService.updateRotatedImageURL(this.rotatedURL, this.slideIndex);
    this.closeButtonClicked.emit();
    this.dialogRef.close();
  }

  private updateImageSrc(): void {
    this.imageList.getValue().forEach((image: string, index: number) => {
      if (index === this.slideIndex) {
        this.imageSrc = image;
      }
    });
  }
}
